/**
 *
 * @module purchaseClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/purchase.md
 * @see https://github.bamtech.co/services-commons/public-api/blob/master/swagger/services/store.md
 *
 */

import Check from 'check-types';
import Token from './../token/entry';
import HttpClient from './../platforms/httpClient';
import Exception from './../exception/entry';
import Logging from './../../logging/entry';
import Util from './../util/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';

import PurchaseActivation from './purchaseActivation';
import PurchaseActivationResult from './purchaseActivationResult';
import PurchaseActivationStatus from './purchaseActivationStatus';
import PurchaseClientEndpoint from './purchaseClientEndpoint';
import PurchaseClientConfiguration from './purchaseClientConfiguration';
import ReceiptCredentials from './receiptCredentials';

const ErrorReason = Exception.ErrorReason;
const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const AccessToken = Token.AccessToken;
const HttpMethod = Configuration.HttpMethod;
const PurchaseClientDustUrnReference = DustUrnReference.services.purchase.purchaseClient;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class PurchaseClient
 * @implements {IPurchaseClient}
 * @desc Provides a data client that can be used to access purchase services.
 *
 */
export default class PurchaseClient {

    /**
     *
     * @param {PurchaseClientConfiguration} purchaseClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(purchaseClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'PurchaseClient(purchaseClientConfiguration, logger, httpClient)',
            get purchaseClientConfiguration() {
                return `${this.ctor} needs PurchaseClientConfiguration instance`;
            }
        };

        instanceStrict(
            purchaseClientConfiguration,
            PurchaseClientConfiguration,
            errorMessage.purchaseClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {PurchaseClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = purchaseClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {String}
         * @desc determines if you should refresh the access token
         * @note Maybe centralize this string if it's going to be used in many places
         *
         */
        this.refreshAccessTokenHeader = 'X-BAMTech-Refresh-Access-Token';

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(PurchaseClient.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {ReceiptCredentails} receiptCredentials - Information about the purchase made in the Apple store.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Redeem just made purchases to watch on the current device.
     * @returns {Promise<PurchaseActivationResult>} Result indicating activation success and any active entitlements.
     *
     */
    redeemPurchases(receiptCredentials, accessToken) {

        const errorMessage = {
            method: 'PurchaseClient.redeemPurchases(receiptCredentials, accessToken)',
            get receiptCredentials() {
                return `${this.method} needs ReceiptCredentials instance`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(receiptCredentials, ReceiptCredentials, errorMessage.receiptCredentials, InvalidArgumentException); // eslint-disable-line
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            PurchaseClientEndpoint.redeemPurchases,
            receiptCredentials
        );

        logger.info(
            PurchaseClient.name,
            `redeemPurchases - receiptCredentials(${JSON.stringify(receiptCredentials, null, 2)})`
        );

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: PurchaseClient.name,
            urn: PurchaseClientDustUrnReference.redeemPurchases,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { refreshAccessTokenHeader } = this;
            const { data = {}, headers } = response;
            const { temporaryAccessGranted = false } = data;
            const { invalid = [], purchases = [] } = data;
            const refreshAccessToken = !!headers.get(refreshAccessTokenHeader);

            const reasons = this.getErrorReasons(invalid);
            const purchaseActivations = this.getPurchaseActivations(purchases);

            return Promise.resolve(
                new PurchaseActivationResult(refreshAccessToken, temporaryAccessGranted, reasons, purchaseActivations)
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @param {ReceiptCredentails} receiptCredentials - Information about the purchase made in the Apple store.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Redeem previously made purchases to watch on the current device.
     * @returns {Promise<PurchaseActivationResult>} Result indicating activation success and any active entitlements.
     *
     */
    restorePurchases(receiptCredentials, accessToken) {

        const errorMessage = {
            method: 'PurchaseClient.restorePurchases(receiptCredentials, accessToken)',
            get receiptCredentials() {
                return `${this.method} needs ReceiptCredentials instance`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(receiptCredentials, ReceiptCredentials, errorMessage.receiptCredentials, InvalidArgumentException); // eslint-disable-line
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;
        const payload = this.getPayload(
            accessToken,
            PurchaseClientEndpoint.restorePurchases,
            receiptCredentials
        );

        logger.info(
            PurchaseClient.name,
            `restorePurchases - receiptCredentials(${JSON.stringify(receiptCredentials, null, 2)})`
        );

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: PurchaseClient.name,
            urn: PurchaseClientDustUrnReference.restorePurchases,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { refreshAccessTokenHeader } = this;
            const { data = {}, headers } = response;
            const { temporaryAccessGranted = false } = data;
            const { invalid = [], purchases = [] } = data;
            const refreshAccessToken = !!headers.get(refreshAccessTokenHeader);
            const reasons = this.getErrorReasons(invalid);
            const purchaseActivations = this.getPurchaseActivations(purchases);

            return Promise.resolve(
                new PurchaseActivationResult(refreshAccessToken, temporaryAccessGranted, reasons, purchaseActivations)
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {PurchaseClientEndpoint} rel
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, receiptCredentials) {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { href, headers } = endpoint;
        const requestHeaders = Object.assign({}, headers);
        const body = JSON.stringify(receiptCredentials.payload());
        const url = href.replace('{store}', receiptCredentials.getStore());

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi, accessToken.token);

        return {
            url,
            body,
            headers: requestHeaders
        };

    }

    /**
     *
     * @access private
     * @param {Object} error
     * @returns {SDK.Services.Exception.ErrorReason}
     *
     */
    getErrorReason(error) {

        let errorReason = null;

        if (Util.getSafe(() => error.code)) {
            errorReason = new ErrorReason(error.code, Util.getSafe(() => error.description));
        }

        return errorReason;

    }

    /**
     *
     * @access private
     * @param {Array<Object>} errors
     * @returns {Array<SDK.Services.Exception.ErrorReason>}
     *
     */
    getErrorReasons(errors) {

        const errorReasons = [];

        if (Check.array(errors)) {

            errors.forEach((error) => {

                const errorReason = this.getErrorReason(error);

                if (Check.assigned(errorReason)) {
                    errorReasons.push(errorReason);
                }

            });

        }

        return errorReasons;

    }

    /**
     *
     * @access private
     * @param {Array<Object>} purchases
     * @returns {Array<SDK.Services.Purchase.PurchaseActivation>}
     *
     */
    getPurchaseActivations(purchases) {

        const purchaseActivations = [];

        purchases.forEach((purchase) => {

            const reason = this.getErrorReason(purchase.reason);
            const status = PurchaseActivationStatus[purchase.status];

            purchaseActivations.push(new PurchaseActivation(purchase.products, purchase.sku, status, reason));

        });

        return purchaseActivations;

    }
}



// WEBPACK FOOTER //
// ./src/services/purchase/purchaseClient.js